/**
 * Modal Component
 * Reusable modal/dialog with backdrop and animations
 *
 * Rendered through a portal onto document.body rather than in place.
 * `position: fixed` is only relative to the viewport while no ancestor
 * establishes a containing block for it — any ancestor with a
 * `transform`, `filter` or `will-change` takes that job over. The chat
 * widget is exactly that case: a Framer Motion panel that animates
 * `scale` and clips with `overflow-hidden`, so an inline modal opened
 * from inside it was sized to the 380x620 panel and then cut off.
 * A portal sidesteps the whole class of problem, and puts the z-50
 * backdrop at the top of the stacking order where it belongs.
 */

import { useCallback, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    showCloseButton?: boolean;
}

export function Modal({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    closeOnBackdrop = true,
    closeOnEscape = true,
    showCloseButton = true,
}: ModalProps) {
    const titleId = useId();
    const handleEscape = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape' && closeOnEscape) {
                onClose();
            }
        },
        [onClose, closeOnEscape]
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleEscape]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-[95vw] h-[90vh]',
    };

    return createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={closeOnBackdrop ? onClose : undefined}
            />

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={title ? titleId : undefined}
                    className={`
                        relative w-full ${sizeClasses[size]} bg-white rounded-xl shadow-xl
                        transform transition-all
                    `}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    {(title || showCloseButton) && (
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            {title && (
                                <h2 id={titleId} className="text-xl font-semibold text-gray-900">{title}</h2>
                            )}
                            {showCloseButton && (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label="Close"
                                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Content */}
                    <div className={`px-6 py-4 ${size === 'full' ? 'overflow-y-auto max-h-[calc(90vh-120px)]' : ''}`}>
                        {children}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default Modal;
